import { ref } from 'vue'
import { defineStore } from 'pinia'

import type { AssayCellCorrection, Metadata } from '@/api'
import type { ChannelTreeOut } from '@/components/project/review/channelReviewController'
import { newRequestId } from '@/utils/uuid'

const PENDING_STORAGE_PREFIX = 'moria.review-draft.pending.v1'
const PENDING_STORAGE_OWNER_KEY = `${PENDING_STORAGE_PREFIX}:owner`

export interface ReviewDraftContent {
  corrections?: Array<Partial<AssayCellCorrection>>
  metadataUpdates?: Array<{ certSlug: string; metadata: Partial<Metadata> }>
  marked_done?: boolean
  finalized?: boolean
  channelTree?: ChannelTreeOut | null
  [key: string]: unknown
}

export interface ReviewDraftOut {
  revision: number
  content: ReviewDraftContent
  [key: string]: unknown
}

interface SaveAttempt {
  content: ReviewDraftContent
  expectedRevision: number
  idempotencyId: string
}

interface PersistedSaveState {
  active?: SaveAttempt
  pending?: SaveAttempt
}

function keyOf(extractionId: string, certSlug: string): string {
  return `${extractionId}:${certSlug}`
}

function storageKey(
  ownerUserId: string,
  projectId: number,
  extractionId: string,
  certSlug: string,
): string {
  return `${PENDING_STORAGE_PREFIX}:${encodeURIComponent(ownerUserId)}:${projectId}:${encodeURIComponent(extractionId)}:${encodeURIComponent(certSlug)}`
}

function storageExtractionPrefix(
  ownerUserId: string,
  projectId: number,
  extractionId: string,
): string {
  return `${PENDING_STORAGE_PREFIX}:${encodeURIComponent(ownerUserId)}:${projectId}:${encodeURIComponent(extractionId)}:`
}

function normalized(content: ReviewDraftContent = {}): ReviewDraftContent {
  return {
    corrections: [...(content.corrections ?? [])],
    metadataUpdates: [...(content.metadataUpdates ?? [])],
    marked_done: content.marked_done ?? false,
    finalized: content.finalized ?? false,
    channelTree: content.channelTree ?? null,
  }
}

function sameContent(left: ReviewDraftContent, right: ReviewDraftContent): boolean {
  return JSON.stringify(normalized(left)) === JSON.stringify(normalized(right))
}

function parseSaveAttempt(value: unknown): SaveAttempt | null {
  if (!value || typeof value !== 'object') return null
  const candidate = value as Partial<SaveAttempt>
  if (
    !Number.isInteger(candidate.expectedRevision) ||
    candidate.expectedRevision! < 0 ||
    typeof candidate.idempotencyId !== 'string' ||
    !candidate.idempotencyId ||
    !candidate.content ||
    typeof candidate.content !== 'object'
  ) {
    return null
  }
  return {
    content: normalized(candidate.content),
    expectedRevision: candidate.expectedRevision!,
    idempotencyId: candidate.idempotencyId,
  }
}

export const useReviewDraftsStore = defineStore('reviewDrafts', () => {
  const drafts = ref<Record<string, ReviewDraftOut | null>>({})
  const contents = ref<Record<string, ReviewDraftContent>>({})
  const pending = ref<Record<string, ReviewDraftContent | null>>({})
  const saving = ref<Record<string, boolean>>({})
  const errors = ref<Record<string, string | null>>({})
  const baselines = new Map<string, ReviewDraftContent>()
  const pendingAttempts = new Map<string, SaveAttempt>()
  const restoredScopes = new Set<string>()
  let ownerUserId: string | null = null

  function clearPersistedSaveStates(): void {
    if (typeof sessionStorage === 'undefined') return
    try {
      for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
        const key = sessionStorage.key(index)
        if (key?.startsWith(`${PENDING_STORAGE_PREFIX}:`)) sessionStorage.removeItem(key)
      }
    } catch {
      // Browser storage cleanup is best effort.
    }
  }

  function clearState(): void {
    drafts.value = {}
    contents.value = {}
    pending.value = {}
    saving.value = {}
    errors.value = {}
    baselines.clear()
    pendingAttempts.clear()
    restoredScopes.clear()
  }

  function setStorageOwner(userId: string | null): void {
    if (ownerUserId === userId) return
    let persistedOwner: string | null = null
    if (typeof sessionStorage !== 'undefined') {
      try {
        persistedOwner = sessionStorage.getItem(PENDING_STORAGE_OWNER_KEY)
      } catch {
        // Treat unavailable browser storage as having no recoverable owner.
      }
    }
    clearState()
    if (!userId || (persistedOwner && persistedOwner !== userId)) clearPersistedSaveStates()
    ownerUserId = userId
    if (typeof sessionStorage === 'undefined') return
    try {
      if (userId) sessionStorage.setItem(PENDING_STORAGE_OWNER_KEY, userId)
      else sessionStorage.removeItem(PENDING_STORAGE_OWNER_KEY)
    } catch {
      // Browser storage ownership is best effort.
    }
  }

  function persistSaveState(projectId: number, extractionId: string, certSlug: string): void {
    if (typeof sessionStorage === 'undefined' || !ownerUserId) return
    const key = keyOf(extractionId, certSlug)
    const queued = pendingAttempts.get(key)
    const persistedKey = storageKey(ownerUserId, projectId, extractionId, certSlug)
    try {
      if (!queued) {
        sessionStorage.removeItem(persistedKey)
        return
      }
      sessionStorage.setItem(persistedKey, JSON.stringify({ pending: queued }))
    } catch {
      // In-memory state and the global unload warning still protect the draft.
    }
  }

  function restoreSaveState(projectId: number, extractionId: string, certSlug: string): void {
    if (typeof sessionStorage === 'undefined' || !ownerUserId) return
    const persistedKey = storageKey(ownerUserId, projectId, extractionId, certSlug)
    if (restoredScopes.has(persistedKey)) return
    restoredScopes.add(persistedKey)
    try {
      const raw = sessionStorage.getItem(persistedKey)
      if (!raw) return
      const parsed = JSON.parse(raw) as Partial<PersistedSaveState>
      const attempt = parseSaveAttempt(parsed.pending) ?? parseSaveAttempt(parsed.active)
      if (!attempt) return
      const key = keyOf(extractionId, certSlug)
      pendingAttempts.set(key, attempt)
      pending.value = { ...pending.value, [key]: attempt.content }
      contents.value = { ...contents.value, [key]: attempt.content }
    } catch {
      // Ignore malformed or unavailable browser storage.
    }
  }

  function draftFor(extractionId: string, certSlug: string): ReviewDraftOut | null {
    return drafts.value[keyOf(extractionId, certSlug)] ?? null
  }

  function contentFor(extractionId: string, certSlug: string): ReviewDraftContent {
    return normalized(contents.value[keyOf(extractionId, certSlug)])
  }

  function hasUnsaved(extractionId: string, certSlug: string): boolean {
    return !!pending.value[keyOf(extractionId, certSlug)]
  }

  function hasUnsavedForExtraction(projectId: number, extractionId: string): boolean {
    const prefix = `${extractionId}:`
    if (Object.entries(pending.value).some(([key, content]) => key.startsWith(prefix) && content)) {
      return true
    }
    if (typeof sessionStorage === 'undefined' || !ownerUserId) return false
    const persistedPrefix = storageExtractionPrefix(ownerUserId, projectId, extractionId)
    try {
      for (let index = 0; index < sessionStorage.length; index += 1) {
        if (sessionStorage.key(index)?.startsWith(persistedPrefix)) return true
      }
    } catch {
      // In-memory state remains authoritative when storage is unavailable.
    }
    return false
  }

  function hasUnsavedWork(): boolean {
    return Object.values(pending.value).some(Boolean) || hasPersistedUnsavedWork()
  }

  function hasPersistedUnsavedWork(): boolean {
    if (typeof sessionStorage === 'undefined') return false
    try {
      const persistedOwner = ownerUserId ?? sessionStorage.getItem(PENDING_STORAGE_OWNER_KEY)
      if (!persistedOwner) return false
      const ownerPrefix = `${PENDING_STORAGE_PREFIX}:${encodeURIComponent(persistedOwner)}:`
      for (let index = 0; index < sessionStorage.length; index += 1) {
        if (sessionStorage.key(index)?.startsWith(ownerPrefix)) return true
      }
    } catch {
      // In-memory state still protects the draft when browser storage is unavailable.
    }
    return false
  }

  async function load(
    projectId: number,
    extractionId: string,
    certSlug: string,
    initial: ReviewDraftContent = {},
  ): Promise<ReviewDraftOut | null> {
    const key = keyOf(extractionId, certSlug)
    restoreSaveState(projectId, extractionId, certSlug)
    errors.value = { ...errors.value, [key]: null }
    if (!baselines.has(key)) baselines.set(key, normalized(initial))
    if (!contents.value[key]) contents.value = { ...contents.value, [key]: normalized(initial) }
    return draftFor(extractionId, certSlug)
  }

  function queue(
    projectId: number,
    extractionId: string,
    certSlug: string,
    content: ReviewDraftContent,
  ): void {
    const key = keyOf(extractionId, certSlug)
    const next = normalized(content)
    const current = pending.value[key] ?? contents.value[key]
    if (current && sameContent(current, next)) return
    contents.value = { ...contents.value, [key]: next }
    pending.value = { ...pending.value, [key]: next }
    pendingAttempts.set(key, {
      content: next,
      expectedRevision: draftFor(extractionId, certSlug)?.revision ?? 0,
      idempotencyId: newRequestId(),
    })
    persistSaveState(projectId, extractionId, certSlug)
  }

  function commitLocal(
    projectId: number,
    extractionId: string,
    certSlug: string,
    content: ReviewDraftContent,
  ): ReviewDraftOut {
    const key = keyOf(extractionId, certSlug)
    const next = normalized(content)
    const draft = {
      revision: (drafts.value[key]?.revision ?? 0) + 1,
      content: next,
    } satisfies ReviewDraftOut
    drafts.value = { ...drafts.value, [key]: draft }
    contents.value = { ...contents.value, [key]: next }
    baselines.set(key, next)
    pending.value = { ...pending.value, [key]: null }
    pendingAttempts.delete(key)
    errors.value = { ...errors.value, [key]: null }
    persistSaveState(projectId, extractionId, certSlug)
    return draft
  }

  async function flush(
    projectId: number,
    extractionId: string,
    certSlug: string,
  ): Promise<ReviewDraftOut | null> {
    const key = keyOf(extractionId, certSlug)
    const queued = pending.value[key]
    if (!queued) return draftFor(extractionId, certSlug)
    saving.value = { ...saving.value, [key]: true }
    try {
      return commitLocal(projectId, extractionId, certSlug, queued)
    } finally {
      saving.value = { ...saving.value, [key]: false }
    }
  }

  async function discard(
    projectId: number,
    extractionId: string,
    certSlug: string,
  ): Promise<ReviewDraftOut | null> {
    const key = keyOf(extractionId, certSlug)
    pending.value = { ...pending.value, [key]: null }
    pendingAttempts.delete(key)
    const draft = draftFor(extractionId, certSlug)
    const restored = draft?.content ?? baselines.get(key)
    contents.value = { ...contents.value, [key]: normalized(restored) }
    errors.value = { ...errors.value, [key]: null }
    persistSaveState(projectId, extractionId, certSlug)
    return draft
  }

  return {
    // Pinia setup stores must return state refs for $subscribe/devtools tracking.
    drafts,
    contents,
    saving,
    errors,
    draftFor,
    contentFor,
    hasUnsaved,
    hasUnsavedForExtraction,
    hasUnsavedWork,
    setStorageOwner,
    load,
    queue,
    flush,
    discard,
  }
})
